import axios from "../utils/axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

export const getFundraisers = createAsyncThunk(
  "fundraiser",
  async (payload, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();

      // Add all available parameters to the query string
      if (payload?.page) queryParams.append("page", payload.page);
      if (payload?.limit) queryParams.append("limit", payload.limit);
      if (payload?.search) queryParams.append("search", payload.search);
      if (payload?.status) queryParams.append("status", payload.status);
      if (payload?.duration) queryParams.append("duration", payload.duration);

      // Build the final URL with all query parameters
      const queryString = queryParams.toString();
      // Always use the listing endpoint that exists on backend
      const url = `fundraiser/all${queryString ? `?${queryString}` : ""}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getFundraiserCategories = createAsyncThunk(
  "fundraiser/category",
  async () => {
    try {
      const response = await axios.get("fundraiser/category");
      return response?.data;
    } catch (error) {
      return error;
    }
  }
);

// Create a user-scoped fundraiser category
export const createFundraiserCategory = createAsyncThunk(
  "fundraiser/category/create",
  async (payload, thunkAPI) => {
    try {
      // payload: { title }
      const response = await axios.post("fundraiser/category", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const getSubCategoriesByCategoryId = createAsyncThunk(
  "fundraiser/subcategory",
  async (payload) => {
    try {
      // Try path param first, then query param as fallback
      const categoryId = payload?.id || payload?.categoryId;
      if (categoryId) {
        try {
          const respPath = await axios.get(`fundraiser/subcategory/${categoryId}`);
          return respPath?.data;
        } catch (_) {
          const respQuery = await axios.get(`fundraiser/subcategory`, { params: { categoryId } });
          return respQuery?.data;
        }
      }
      const response = await axios.get("fundraiser/subcategory");
      return response?.data;
    } catch (error) {
      return error;
    }
  }
);
export const getFundraisersBySubCategoryId = createAsyncThunk(
  "fundraiser/bysubcategory",
  async (payload) => {
    try {
      // Backend contract: { "subCategories": ["id1", "id2", ...] }
      const arr = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.subCategories)
        ? payload.subCategories
        : Array.isArray(payload?.subCategoryIds)
        ? payload.subCategoryIds
        : Array.isArray(payload?.subCategoryId)
        ? payload.subCategoryId
        : payload?.subCategoryId
        ? [payload.subCategoryId]
        : [];

      const resp = await axios.post("fundraiser/bysubcategory", { subCategories: arr });
      return resp?.data;
    } catch (error) {
      return error;
    }
  }
);

export const getFundsDeposited = createAsyncThunk(
  "fundraiser/funds",
  async (payload, thunkAPI) => {
    try {
      const searchQuery = typeof payload === 'string' ? payload : (payload?.search || '');
      const response = await axios.get(
        `fundraiser/funds?search=${searchQuery}`
      );
      return response?.data;
    } catch (error) {
      const silent = typeof payload === 'object' && payload?.silent === true;
      return thunkAPI.rejectWithValue({ 
        ...(error?.response?.data || {}), 
        message: error?.response?.data?.message || error?.message || 'Error loading funds',
        silent: silent 
      });
    }
  }
);

export const addBank = createAsyncThunk(
  "payout/bank",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post("payout/bank", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getBanks = createAsyncThunk(
  "payout/banks",
  async (searchQuery = "") => {
    try {
      const response = await axios.get(`payout/banks?search=${searchQuery}`);
      return response?.data;
    } catch (error) {
      return Promise.reject(error?.response?.data || error);
    }
  }
);

export const getWithdrawals = createAsyncThunk(
  "payout/fundraiser",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(
        payload?.endingBefore
          ? `payout/fundraiser?endingBefore=${payload?.endingBefore}`
          : payload?.startingAfter
          ? `payout/fundraiser?startingAfter=${payload?.startingAfter}`
          : "payout/fundraiser"
      );
      return response?.data;
    } catch (error) {
      // Check if silent flag is passed
      const silent = payload?.silent === true;
      return thunkAPI.rejectWithValue({ 
        ...(error?.response?.data || {}), 
        message: error?.response?.data?.message || error?.message || 'Error loading withdrawals',
        silent: silent 
      });
    }
  }
);

export const createTransfer = createAsyncThunk(
  "payout/transfer",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post("payout/transfer", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const createConnectedAccount = createAsyncThunk(
  "payout/link",
  async () => {
    try {
      const response = await axios.get("payout/link");
      return response?.data;
    } catch (error) {
      return Promise.reject(error?.response?.data || error);
    }
  }
);

export const getOnboardingStatus = createAsyncThunk(
  "payout/onboardingStatus",
  async () => {
    try {
      const response = await axios.get("payout/onboardingStatus");
      return response?.data;
    } catch (error) {
      return Promise.reject(error?.response?.data || error);
    }
  }
);

export const getAccountDetails = createAsyncThunk(
  "payout/balance",
  async () => {
    try {
      const response = await axios.get("payout/balance");
      return response?.data;
    } catch (error) {
      return Promise.reject(error?.response?.data || error);
    }
  }
);

export const updateReferredBy = createAsyncThunk(
  "auth/updateReferredBy",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.patch("auth/updateReferredBy", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getFundraiserInquiries = createAsyncThunk(
  "inquiry/fundraiser",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(
        `inquiry/fundraiser?page=${payload?.page}`
      );
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const addContent = createAsyncThunk(
  "fundraiser/updateContent",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.patch("fundraiser/updateContent", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getFundraiserContent = createAsyncThunk(
  "fundraiser/content",
  async (payload, thunkAPI) => {
    try {
      const url = `fundraiser/content/${payload?.userId}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const toggleFundraiserStatus = createAsyncThunk(
  "fundraiser/status",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(`fundraiser/status/${payload?.id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const referMerchant = createAsyncThunk(
  "fundraiser/referMerchant",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post("fundraiser/referMerchant", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getFundraiserReferrals = createAsyncThunk(
  "fundraiser/referdMerchants/user",
  async (payload, thunkAPI) => {
    try {
      // Build the query parameters
      const queryParams = new URLSearchParams();

      // Add all available parameters to the query string
      if (payload?.page) queryParams.append("page", payload.page);
      if (payload?.limit) queryParams.append("limit", payload.limit);
      if (payload?.search) queryParams.append("search", payload.search);
      if (payload?.status) queryParams.append("status", payload.status);
      if (payload?.duration) queryParams.append("duration", payload.duration);

      // Build the final URL with all query parameters
      const queryString = queryParams.toString();
      const url = queryString
        ? `fundraiser/referdMerchants/user?${queryString}`
        : "fundraiser/referdMerchants/user";

      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getDashboardStats = createAsyncThunk(
  "fundraiser/getDashboardStats",
  async (timePeriod = "", thunkAPI) => {
    try {
      const response = await axios.get(
        `fundraiser/getDashboardStats?timePeriod=${timePeriod}`
      );
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ ...(error?.response?.data || {}), message: (error?.response?.data?.message || error?.message || 'Error'), silent: true });
    }
  }
);

export const getTransactions = createAsyncThunk(
  "fundraiser/transactions",
  async (payload, thunkAPI) => {
    try {
      const filter = typeof payload === 'string' ? payload : (payload?.filter || '');
      const response = await axios.get(
        `fundraiser/transactions?filter=${filter}`
      );
      return response?.data;
    } catch (error) {
      // Check if silent flag is passed
      const silent = typeof payload === 'object' && payload?.silent === true;
      return thunkAPI.rejectWithValue({ 
        ...(error?.response?.data || {}), 
        message: error?.response?.data?.message || error?.message || 'Error loading transactions',
        silent: silent 
      });
    }
  }
);

export const getMerchantById = createAsyncThunk(
  "merchant/get",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(`merchant/${payload?.id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const createTarget = createAsyncThunk(
  "fundraiser/createTarget",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post("fundraiser/createTarget", payload);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const updateTarget = createAsyncThunk(
  "fundraiser/updateTarget",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.patch(
        `fundraiser/target/${payload?.id}`,
        payload
      );
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getTarget = createAsyncThunk("fundraiser/getTarget", async (_, thunkAPI) => {
  try {
    const response = await axios.get("fundraiser/target");
    return response?.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error?.response?.data || error);
  }
});

export const getFundraiserById = createAsyncThunk(
  "fundraiser/get",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(`fundraiser/${payload?.id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const bulkStatusUpdate = createAsyncThunk(
  "fundraiser/bulk-update-status",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.patch("fundraiser/bulk-update-status", payload);
      
      // If the status is being set to "approved", send email notifications
      if (payload?.status?.toLowerCase() === "approved") {
        try {
          // Send email notifications to the fundraisers
          await axios.post("admin/sendFundraiserApprovalEmail", {
            fundraiserIds: payload.fundraiserIds
          });
        } catch (emailError) {
          // Log the email error but don't fail the overall operation
          console.error("Failed to send fundraiser approval emails:", emailError);
        }
      }
      
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// Admin: Update fundraiser status (approve/decline/pending)
export const approveOrRejectFundraiser = createAsyncThunk(
  "admin/updateFundraiserStatus",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post("admin/updateFundraiserStatus", payload);
      
      // If the status is being set to "approved", send an email notification
      if (payload?.status?.toLowerCase() === "approved") {
        try {
          // Send email notification to the fundraiser
          await axios.post("admin/sendFundraiserApprovalEmail", {
            fundraiserIds: payload.ids
          });
        } catch (emailError) {
          // Log the email error but don't fail the overall operation
          console.error("Failed to send fundraiser approval email:", emailError);
        }
      }
      
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getAllTargets = createAsyncThunk(
  "fundraiser/allTargets",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(`fundraiser/allTargets/${payload?.id}/${payload?.year}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// Delete Fundraiser (pending only) by id
export const deleteFundraiserById = createAsyncThunk(
  "fundraiser/delete",
  async (payload, thunkAPI) => {
    try {
      // payload: { id }
      const response = await axios.patch(`admin/${payload?.id}/delete`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

// Fetch the current fundraiser's profile (self)
export const getFundraiserProfile = async () => {
  try {
    const response = await axios.get('fundraiser/profile', {
      headers: { 'Cache-Control': 'no-cache' },
      params: { _: Date.now() },
    }); 
    return response?.data;
  } catch (error) {
    throw error;
  }
};

// Update the current fundraiser's profile (self)
export const updateFundraiserProfile = async (data) => {
  try {
    const response = await axios.patch('fundraiser/profile', data);
    return response?.data;
  } catch (error) {
    throw error;
  }
};